import {useCallback} from "react";

import {
    setExperimentFeatureValues,
    setPromptVariable,
} from "@khan/guide-core-helpers";
import {GuideStateApi} from "@khan/guide-state";
import Log, {Errors} from "@khan/logging";

import {useGetGrowthbookFeaturesAsPromptVariables} from "./use-get-growthbook-features-as-prompt-variables.ts";
import {useSendGuideEvent} from "./use-send-guide-event.ts";

interface InitializeGuideOptions {
    assignmentID?: string;
}

interface InitializeGuideFn {
    /**
     * Initialize the AI Guide.
     *
     * @returns A promise that resolves when the AI Guide has been initialized.
     */
    (options?: InitializeGuideOptions): Promise<void>;
}

/**
 * Provide function that will initialize the AI guide.
 *
 * @returns A function that will initialize the AI guide.
 */
export const useInitializeGuide = (): InitializeGuideFn => {
    const sendEvent = useSendGuideEvent();
    const getGrowthbookFeaturesAsPromptVariables =
        useGetGrowthbookFeaturesAsPromptVariables();

    const initializeGuide = useCallback(
        (options?: InitializeGuideOptions): Promise<void> => {
            if (GuideStateApi.latest().initialized) {
                return Promise.resolve();
            }

            // If there is already an ongoing initialization, return the promise.
            // This will ensure that we don't send multiple initialization events.
            let initializationPromise =
                GuideStateApi.latest().initializationPromise;
            if (initializationPromise) {
                return initializationPromise;
            }

            const initialize = async (): Promise<void> => {
                try {
                    GuideStateApi.set("isLoading", true);
                    await sendEvent("initial_load", {
                        assignmentID: options?.assignmentID,
                    });
                    // We only set initialized to true if the event was sent
                    // successfully.
                    GuideStateApi.set("initialized", true);

                    const growthbookVariables =
                        await getGrowthbookFeaturesAsPromptVariables(
                            GuideStateApi.latest().threadId,
                        );
                    for (const [key, value] of Object.entries(
                        growthbookVariables.promptVariables,
                    )) {
                        setPromptVariable(key, value);
                    }

                    setExperimentFeatureValues(
                        growthbookVariables.experimentFeatures,
                    );
                } catch (error: any) {
                    Log.error(
                        "Failed to send initial_load event to ai-guide.",
                        Errors.TransientKhanService,
                        {cause: error},
                    );
                } finally {
                    GuideStateApi.set("isLoading", false);
                    GuideStateApi.set("initializationPromise", null);
                }
            };

            initializationPromise = initialize();
            GuideStateApi.set("initializationPromise", initializationPromise);
            return initializationPromise;
        },
        [sendEvent, getGrowthbookFeaturesAsPromptVariables],
    );

    return initializeGuide;
};
